import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { formatNumber, titleCase } from '../../lib/format'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton, StatusBadge,
} from '../../components/ui'
import DownloadButton from '../../components/DownloadButton'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, PlusIcon } from '../../components/icons'

interface LandParcel {
  id?: string
  name?: string
  region_id?: string
  district_id?: string
  area_size_ha?: number
  status?: string
  allocated_org_id?: string
}

const MINERALS = ['Gold', 'Silver', 'Diamond', 'Copper', 'Bauxite', 'Iron Ore', 'Manganese', 'Chromite', 'Coltan', 'Lithium', 'Cobalt', 'Other']
const LAND_ADMIN_ROLES = new Set(['super_admin', 'gov_director', 'gov_regional'])

export default function LandParcels() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [minerals, setMinerals] = useState<string[]>([])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'parcels'],
    queryFn: () => api.get<{ parcels: LandParcel[]; count: number }>('/parcels'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/gov/parcels', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'parcels'] })
      setShowForm(false)
      setMinerals([])
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      name: String(form.get('name') ?? '').trim(),
      region_id: String(form.get('region_id') ?? '').trim(),
      district_id: String(form.get('district_id') ?? '').trim(),
      area_size_ha: Number(form.get('area_size_ha') ?? 0),
      eligible_minerals: minerals,
    })
  }

  const parcels = data?.parcels ?? []
  const canCreate = LAND_ADMIN_ROLES.has(user?.role ?? '')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Land parcels</h1>
          <p className="page-head__sub">Government-managed land available for allocation.</p>
        </div>
        <div className="row">
          <DownloadButton path="/api/v1/gov/exports/land-parcels.xlsx" filename="land_parcels_report.xlsx">
            Export Excel
          </DownloadButton>
          <DownloadButton path="/api/v1/gov/exports/land-parcels.pdf" filename="land_parcels_report.pdf">
            Export PDF
          </DownloadButton>
          {canCreate && (
            <>
              <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
                {showForm ? 'Cancel' : 'New parcel'}
              </Button>
              <button
                type="button" className="fab"
                onClick={() => setShowForm((s) => !s)}
                aria-label={showForm ? 'Cancel' : 'New parcel'}
              >
                {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
              </button>
            </>
          )}
        </div>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="New land parcel" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not create that parcel.'}
                  </Alert>
                )}
                {/* PWA: 2 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Parcel & location', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                <Input label="Name" name="name" required />
                <div className="grid grid--2">
                  <Input label="Province / region" name="region_id" required />
                  <Input label="District" name="district_id" />
                </div>
                      </div>
                    ) },
                    { title: 'Size & minerals', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                <Input label="Area (hectares)" name="area_size_ha" type="number" step="any" min="0" required />
                <div>
                  <label className="field__label" style={{ display: 'block', marginBottom: 6 }}>Eligible minerals</label>
                  <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                    {MINERALS.map((m) => (
                      <label key={m} className={`radio-opt${minerals.includes(m) ? ' radio-opt--checked' : ''}`}
                             style={{ padding: 'var(--sp-2) var(--sp-3)' }}>
                        <input
                          type="checkbox"
                          checked={minerals.includes(m)}
                          onChange={() => setMinerals((cur) => (
                            cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]
                          ))}
                        />
                        {m}
                      </label>
                    ))}
                  </div>
                </div>
                      </div>
                    ) },
                  ]}
                  footer={(
                <div className="row">
                  <Button type="submit" loading={create.isPending}>Create parcel</Button>
                </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Parcels" sub={parcels.length ? `${parcels.length} on file` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }}><Skeleton h={20} /></div>
          ) : parcels.length === 0 ? (
            <EmptyState title="No parcels yet" body="Land parcels created for allocation will appear here." />
          ) : (
            <>
              {/* PWA tap rows (Status clipped off the 4-col table at 390px);
                  desktop keeps the table. */}
              <div className="mobile-only">
                {parcels.map((p) => (
                  <Link key={p.id} to={`/parcels/${p.id}`} className="lrow">
                    <div className="lrow__main">
                      <div className="lrow__title">{p.name}</div>
                      <div className="lrow__sub">
                        {[[titleCase(p.district_id), titleCase(p.region_id)].filter(Boolean).join(', '),
                          `${formatNumber(p.area_size_ha, 1)} ha`].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="lrow__end"><StatusBadge status={p.status || 'available'} /></div>
                  </Link>
                ))}
              </div>
              <div className="table-wrap desktop-only">
                <table className="table">
                  <thead><tr><th>Parcel</th><th>Location</th><th className="table__num">Area</th><th>Status</th></tr></thead>
                  <tbody>
                    {parcels.map((p) => (
                      <tr key={p.id}>
                        <td><Link to={`/parcels/${p.id}`} style={{ fontWeight: 600 }}>{p.name}</Link></td>
                        <td>{[titleCase(p.district_id), titleCase(p.region_id)].filter(Boolean).join(', ') || '—'}</td>
                        <td className="table__num">{formatNumber(p.area_size_ha, 1)} ha</td>
                        <td><StatusBadge status={p.status || 'available'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
